export const termsConfig = {
  lastUpdated: "2025-01-27",
  title: "Terms of Service",
  intro: "Welcome to LiveLoveSpain. By accessing our website and using our services, you agree to these terms and conditions.",
  sections: [
    {
      title: "1. Acceptance of Terms",
      content:
        "By accessing and using LiveLoveSpain's services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service."
    },
    {
      title: "2. Description of Service",
      content:
        "LiveLoveSpain provides a comprehensive platform to assist individuals and families with their relocation process to Spain. Services include document management, task tracking, personalized recommendations, informational resources, interactive tools and calculators, and guidance materials. Our platform features various tools designed to help users understand Spanish immigration requirements, calculate costs, and plan their move. While we strive to provide accurate and up-to-date information, we cannot guarantee its accuracy. Users should always verify critical information with official government sources."
    },
    {
      title: "3. User Accounts",
      subsections: [
        {
          title: "3.1 Account Creation",
          content:
            "You must create an account to use our services. You are responsible for maintaining the confidentiality of your account information and for all activities carried out under your account. Creating multiple accounts, providing false information, or engaging in fraudulent signup practices is strictly prohibited and may result in immediate account termination and potential legal action."
        },
        {
          title: "3.2 Age Restrictions",
          content:
            "Our platform is intended for use by individuals aged 18 and older. Users under the age of 18 are strictly prohibited from creating accounts or using the platform. LiveLoveSpain complies with the Children's Online Privacy Protection Act (COPPA) and does not knowingly collect information from minors."
        },
        {
          title: "3.3 Account Suspension or Termination",
          content:
            "We reserve the right to suspend or terminate your account for violations of these Terms, non-payment, or prohibited conduct. Suspensions may be temporary or permanent, at LiveLoveSpain's discretion."
        }
      ]
    },
    {
      title: "4. User Responsibilities",
      subsections: [
        {
          title: "4.1 Accurate Information",
          content:
            "You agree to provide accurate, current, and complete information during registration and while using our services. This includes information about your personal immigration situation used to tailor recommendations."
        },
        {
          title: "4.2 Lawful Use",
          content: "You agree to use our services only for lawful purposes and in accordance with these Terms."
        },
        {
          title: "4.3 Prohibited Conduct",
          content:
            "Users must not engage in activities that disrupt platform functionality, violate laws, or infringe on intellectual property rights. Attempts to hack, reverse-engineer, or abuse the platform will result in account termination."
        },
        {
          title: "4.4 Security Obligations",
          content:
            "Users are responsible for safeguarding their accounts, including the use of strong passwords and not sharing login credentials. Any unauthorized access to your account must be reported to LiveLoveSpain immediately."
        }
      ]
    },
    {
      title: "5. Privacy and Data Collection",
      content:
        "Your use of our services is governed by our Privacy Policy. LiveLoveSpain collects and uses your email address and general information about your immigration situation to provide personalized services. We implement industry-standard measures to protect user data but cannot guarantee complete security. Users retain ownership of their personal data, and data submitted may be retained for legal or operational purposes even after account deletion."
    },
    {
      title: "6. Billing and Payment",
      subsections: [
        {
          title: "6.1 Subscription Plans",
          content:
            "We offer three subscription options: a monthly plan, a yearly plan, and a lifetime access plan. The monthly plan is priced at €4.99/month with automatic renewal. The yearly plan is priced at €49/year. The lifetime access plan is available for a one-time payment of €149 (€99 for early access subscribers). All plans provide full access to platform features. A 3-day free trial is available for new users."
        },
        {
          title: "6.2 Auto-Renewal",
          content:
            "Monthly and yearly subscriptions automatically renew unless cancelled. Auto-renewal can be disabled through the subscription management interface. Users will be notified via email before any renewal charge. Lifetime access plans do not require renewal."
        },
        {
          title: "6.3 Plan Changes",
          content:
            "Users can upgrade from monthly to yearly plans or to lifetime access at any time. Downgrades from yearly to monthly plans are not permitted. When upgrading from a monthly or yearly plan to lifetime access, the new plan takes effect immediately."
        },
        {
          title: "6.4 Refunds and Cancellations",
          content:
            "Subscription fees are non-refundable once charged. Users may cancel their subscription at any time, but will retain access until the end of their current billing period. Early cancellation does not entitle users to a refund of the unused portion of their subscription."
        },
        {
          title: "6.5 Free Trial",
          content:
            "New users are eligible for a 3-day free trial. During the trial period, users have full access to all platform features. If users subscribe during their trial period, any remaining trial days will be automatically added to their subscription period, ensuring no trial days are lost. If users do not cancel before the trial period ends, they will be automatically charged for their selected subscription plan."
        },
        {
          title: "6.6 Price Changes",
          content:
            "LiveLoveSpain reserves the right to modify subscription prices at any time. Any price changes will be communicated to users in advance and will only affect future billing cycles. Existing lifetime access purchases will not be affected by price changes."
        }
      ]
    },
    {
      title: "7. Updates and New Features",
      content:
        "LiveLoveSpain regularly updates its platform to improve functionality and add new features. These updates are part of the subscription at no additional cost. New tasks, steps, or services will be labeled as 'New' for user visibility."
    },
    {
      title: "8. User-Generated Content",
      subsections: [
        {
          title: "8.1 Licensing of Content",
          content:
            "By submitting content to LiveLoveSpain, you grant us a non-exclusive, royalty-free, perpetual license to use, modify, and distribute your content as part of our services. You are responsible for ensuring that your content does not infringe on any third-party rights or violate laws."
        },
        {
          title: "8.2 Ownership of Content",
          content:
            "Users retain ownership of their content but grant LiveLoveSpain the right to use it for operational and service-related purposes. Uploaded content must comply with applicable laws and platform guidelines."
        }
      ]
    },
    {
      title: "9. Service Continuity and Discontinuation",
      subsections: [
        {
          title: "9.1 Service Continuity",
          content:
            "We reserve the right to discontinue or modify the platform at any time due to business, operational, or legal reasons. Lifetime access does not imply the service will operate indefinitely. Factors such as market conditions, financial sustainability, and unforeseen circumstances may result in the cessation of services."
        },
        {
          title: "9.2 Platform Discontinuation",
          content:
            "In the event of platform discontinuation, users with lifetime access will receive a prorated refund based on the duration the service was accessible. Users will be notified at least 60 days in advance of any such discontinuation."
        },
        {
          title: "9.3 Acquisition or Merger",
          content:
            "In the event of an acquisition or merger, the acquiring entity may assume responsibility for honoring lifetime access or may offer equivalent services. Users will be notified of any changes to their access rights resulting from such events."
        },
        {
          title: "9.4 Significant Service Changes",
          content:
            "If core features are removed or significantly modified, affected users may receive compensation, alternative services, or refunds at the company's discretion. Users will be notified at least 60 days in advance of significant changes affecting lifetime access rights."
        }
      ]
    },
    {
      title: "10. Third-Party Services",
      subsections: [
        {
          title: "10.1 Third-Party Services Overview",
          content:
            "LiveLoveSpain may include references, links, or access to third-party websites, products, or services ('Third-Party Services') for your convenience. These Third-Party Services are not under our control, and we do not endorse, guarantee, or assume responsibility for their content, functionality, or practices."
        },
        {
          title: "10.2 No Endorsement or Warranty",
          content:
            "We do not provide, control, recommend, or endorse any Third-Party Services. We disclaim all liability arising from failures or losses caused by or relating to Third-Party Services."
        },
        {
          title: "10.3 User Responsibility",
          content:
            "It is your sole responsibility to conduct due diligence and evaluate the suitability, legality, and appropriateness of any Third-Party Services before engaging with them. This includes verifying information through official sources and understanding the terms of service of any third-party providers."
        },
        {
          title: "10.4 Limitation of Liability",
          content:
            "We shall not be liable for any damages, losses, or issues arising from your use of or reliance on any Third-Party Services. Your interactions with such services are solely between you and the third party. By using our platform, you agree to release LiveLoveSpain from any claims or disputes that may arise from your engagement with Third-Party Services."
        },
        {
          title: "10.5 Integration Services",
          content:
            "Where our platform integrates with third-party services, such as payment processors or authentication providers, these integrations are subject to the respective third party's terms of service and privacy policies. Users should review these terms before utilizing such integrated services."
        }
      ]
    },
    {
      title: "11. Disclaimer of Warranties and Accuracy",
      subsections: [
        {
          title: "11.1 General Disclaimer",
          content:
            "LiveLoveSpain is provided 'as is' and 'as available.' We do not warrant that the platform will be uninterrupted, error-free, or meet user expectations."
        },
        {
          title: "11.2 Accuracy and Completeness",
          content:
            "While we strive to provide accurate, up-to-date, and comprehensive information to assist users with their relocation and life in Spain, we make no representations or warranties of any kind, express or implied, about the accuracy, reliability, completeness, suitability, or availability of the information, products, services, or related graphics contained within the app for any purpose."
        },
        {
          title: "11.3 No Warranty",
          content:
            "The information provided is for general informational purposes only and is subject to change without notice. We do not warrant the accuracy, completeness, or usefulness of this information. Any reliance you place on such information is strictly at your own risk."
        },
        {
          title: "11.4 User Responsibility",
          content:
            "It is your responsibility to verify any information obtained from or through this app before relying on it. We recommend consulting official sources when making decisions based on the information provided."
        },
        {
          title: "11.5 Information Updates",
          content:
            "Users should be aware that laws, regulations, and procedures regarding relocation and life in Spain may change. While we endeavor to update information regularly, we cannot guarantee that all information is current at any given time."
        }
      ]
    },
    {
      title: "12. Limitation of Liability",
      content:
        "To the maximum extent permitted by law, LiveLoveSpain and its affiliates are not liable for any indirect, incidental, special, consequential, or punitive damages. Liability for direct damages is limited to the total fees paid by the user in the past 12 months."
    },
    {
      title: "13. Dispute Resolution",
      content:
        "If you have a dispute with LiveLoveSpain, you agree to attempt to resolve it informally through our support team at support@livelovespain.com. If informal resolution is unsuccessful, disputes will be resolved through arbitration in Spain, under Spanish jurisdiction."
    },
    {
      title: "14. Governing Law and Jurisdiction",
      content:
        "These Terms are governed by the laws of Spain. Any disputes arising from these Terms will be subject to the exclusive jurisdiction of the courts in Spain."
    },
    {
      title: "15. Communications",
      content:
        "LiveLoveSpain will communicate with users primarily via email for notifications, updates, and support inquiries. It is your responsibility to ensure that your email address is accurate and current."
    },
    {
      title: "16. Accessibility",
      content:
        "LiveLoveSpain is committed to making its platform accessible to all users, including those with disabilities. We continually strive to comply with accessibility standards and welcome feedback to improve user experience."
    },
    {
      title: "17. Contact Information",
      content:
        "For any questions about these Terms of Service, please contact us at support@livelovespain.com."
    },
    {
      title: "18. Cost Explorer (Beta)",
      subsections: [
        {
          title: "18.1 Beta Status",
          content: 
            "The Cost Explorer feature is currently in beta version. As a beta service, it may contain bugs, inaccuracies, or undergo significant changes. Users acknowledge that they are using a pre-release version that is still under development and refinement."
        },
        {
          title: "18.2 Data Accuracy and Updates",
          content:
            "While we strive to maintain current data, cost information may not always reflect real-time market conditions. Data is sourced from various public sources including the Instituto Nacional de Estadística (INE) and is updated periodically. However, there may be delays between market changes and our data updates."
        },
        {
          title: "18.3 Estimates and Averages",
          content:
            "All costs provided are averages and approximations based on general market data. These figures are not indicative of or guaranteed for any specific individual's situation. Actual costs can vary significantly based on personal choices, lifestyle preferences, specific locations within cities, market fluctuations, and numerous other factors not captured in our calculations."
        },
        {
          title: "18.4 User Responsibility",
          content:
            "Users must not rely solely on the Cost Explorer for making financial or relocation decisions. The tool is intended to provide general guidance only. Users are strongly encouraged to conduct their own research, consult local experts, and verify current prices in their specific areas of interest before making any decisions."
        },
        {
          title: "18.5 Feedback and Improvements",
          content:
            "We actively welcome user feedback to improve the accuracy and functionality of the Cost Explorer. Users can submit feedback through the provided feedback form. We will review all feedback and consider it for future improvements."
        },
        {
          title: "18.6 Limitation of Liability",
          content:
            "LiveLoveSpain expressly disclaims any responsibility for financial or other decisions made based on Cost Explorer data. We are not liable for any discrepancies between estimated and actual costs, or for any consequences of decisions made using this tool. Users acknowledge that the Cost Explorer is an informational resource only and not a financial planning or advisory service."
        }
      ]
    },
    {
      title: "19. AI Immigration & Tax Assistant",
      subsections: [
        {
          title: "19.1 Nature of AI Assistance",
          content: "The AI Immigration & Tax Assistant provides general guidance and information based on available data. While it strives for accuracy, its responses should not be considered legal advice and should be verified with official sources."
        },
        {
          title: "19.2 Usage Guidelines",
          content: "Users should not share sensitive personal information with the AI Assistant. The assistant is designed to provide general immigration guidance and should not be used for submitting official documentation or making final legal decisions."
        },
        {
          title: "19.3 Limitations",
          content: "The AI Assistant's responses are based on its training data and may not reflect the most recent changes in immigration laws or procedures. Users should always verify critical information with official government sources or legal professionals."
        }
      ]
    },
    {
      title: "20. Interactive Tools and Calculators",
      subsections: [
        {
          title: "20.1 Tool Availability",
          content: "LiveLoveSpain provides various interactive tools and calculators designed to assist users with Spanish immigration, financial planning, and relocation processes. These tools include but are not limited to: IPREM calculators, tax calculators, cost estimators, visa requirement checkers, and lifestyle assessment tools."
        },
        {
          title: "20.2 Tool Accuracy and Limitations",
          content: "All tools and calculators are provided for informational and planning purposes only. While we strive to base our calculations on official Spanish government data and current regulations, these tools provide estimates and approximations. Results should not be considered as official determinations or legal advice. Actual requirements, costs, and outcomes may vary significantly based on individual circumstances, changes in regulations, and other factors beyond our control."
        },
        {
          title: "20.3 User Responsibility for Tool Usage",
          content: "Users are responsible for verifying all calculations and results obtained from our tools with official sources before making any decisions. The tools are intended to provide general guidance and should not be the sole basis for financial, legal, or immigration decisions. Users should refer to official sources or consult with qualified professionals if needed for official advice and verification."
        },
        {
          title: "20.4 Tool Updates and Maintenance",
          content: "We regularly update our tools to reflect changes in Spanish laws, regulations, and official data. However, there may be delays between regulatory changes and tool updates. Users should always verify current requirements with official government sources. We reserve the right to modify, discontinue, or replace any tool at any time without prior notice."
        },
        {
          title: "20.5 Tool Data and Privacy",
          content: "Information entered into our tools may be processed to provide calculations and results. While we do not store personal data entered into tools for long-term purposes, tool usage may be logged for service improvement and troubleshooting. Users should not enter sensitive personal information unless necessary for tool functionality."
        },
        {
          title: "20.6 Limitation of Liability for Tools",
          content: "LiveLoveSpain expressly disclaims any responsibility for decisions made based on tool results or calculations. We are not liable for any financial losses, legal issues, or other consequences arising from reliance on our tools. Users acknowledge that these tools are informational resources only and not professional advisory services."
        }
      ]
    },
    {
      title: "21. Affiliate Links and Partnerships",
      subsections: [
        {
          title: "21.1 Affiliate Disclosure",
          content: "LiveLoveSpain may include affiliate links to third-party products, services, or websites. These links may earn us a commission when users make purchases or take certain actions through these links. We are committed to transparency and will clearly disclose affiliate relationships where applicable."
        },
        {
          title: "21.2 Affiliate Link Usage",
          content: "Affiliate links are provided for user convenience and may offer special pricing or benefits. However, users are under no obligation to use these links. Users can choose to access the same products or services directly through the third-party provider's website without using our affiliate links."
        },
        {
          title: "21.3 No Additional Cost to Users",
          content: "Use of affiliate links does not result in any additional cost to users. In fact, affiliate partnerships may provide users with special discounts, exclusive offers, or other benefits not available through direct purchase. Any commissions earned through affiliate links are paid by the third-party provider, not by users."
        },
        {
          title: "21.4 Affiliate Link Accuracy",
          content: "While we strive to ensure affiliate links are accurate and functional, we cannot guarantee their availability or accuracy at all times. Third-party websites may change their URLs, discontinue products, or modify their affiliate programs without notice. Users should verify any offers or pricing directly with the third-party provider."
        },
        {
          title: "21.5 Affiliate Partner Selection",
          content: "We carefully select affiliate partners based on their relevance to our users' needs and the quality of their products or services. However, we do not endorse or guarantee the quality, reliability, or suitability of any third-party products or services. Users should conduct their own research before making purchases."
        },
        {
          title: "21.6 Affiliate Link Privacy",
          content: "When users click on affiliate links, they may be redirected to third-party websites. These websites have their own privacy policies and terms of service. Users should review these policies before providing any personal information or making purchases. LiveLoveSpain is not responsible for the privacy practices of third-party websites."
        }
      ]
    },
    {
      title: "22. Multilingual Content and Translation Accuracy",
      subsections: [
        {
          title: "22.1 Automated Translation Services",
          content: "LiveLoveSpain offers content in multiple languages (English, Spanish, and French) to serve our diverse user base. Our articles, guides, tools, and other informational content are originally written in English and translated into other languages using automated translation services powered by Google Translate API and other translation technologies."
        },
        {
          title: "22.2 Translation Accuracy and Limitations",
          content: "While we strive to provide accurate translations, automated translation services have inherent limitations and may not always capture the full meaning, nuance, cultural context, or technical terminology of the original English content. Translations may contain errors, inaccuracies, or ambiguities that could affect the understanding of the information provided."
        },
        {
          title: "22.3 English as the Authoritative Version",
          content: "In the event of any discrepancies, inconsistencies, or conflicts between the English version of our content and any translated version, the English version shall prevail and be considered the authoritative and legally binding version. Users are encouraged to refer to the English version for critical information or when clarification is needed."
        },
        {
          title: "22.4 User Responsibility for Translation Interpretation",
          content: "Users accessing content in languages other than English acknowledge and accept the limitations of automated translations. You are responsible for verifying critical information with official sources, qualified professionals, or by consulting the original English version. LiveLoveSpain is not liable for any misunderstandings, errors, or consequences arising from reliance on translated content."
        },
        {
          title: "22.5 Continuous Improvement",
          content: "We are committed to improving the quality of our translations over time. If you identify translation errors or have suggestions for improvement, please contact us at support@livelovespain.com. However, we cannot guarantee immediate corrections or updates to all translated content."
        },
        {
          title: "22.6 Professional Translation Recommendations",
          content: "For legal, immigration, tax, or other matters where precise understanding is critical to your situation, we strongly recommend consulting with qualified professionals who can provide guidance in your preferred language, rather than relying solely on automated translations of our general informational content."
        }
      ]
    }
  ]
};
